import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useTeacherActivitySessionUnsubmitterNotificate } from 'src/container/teacher-activity-session-unsubmitter-notificate';
import { useSessionDownloadData } from 'src/hooks/useSessionDownloadData';
import { meState, toastState } from 'src/store';
import { exportToExcel } from 'src/util/download-excel';
import { PopupModal } from '../PopupModal';
import { ButtonV2 } from '../common/ButtonV2';
import { Checkbox } from '../common/Checkbox';
import { Typography } from '../common/Typography';
import { Table } from '../common/table/Table';
import { TableBody } from '../common/table/TableBody';
import { TableHeader } from '../common/table/TableHeader';
import type { CellTypes, TableRowTypes } from '../common/table/type';

const FIXED_COLUMN_WIDTHS = [80, 40, 40, 40, 72, 64, 128] as const;
const FIXED_COLUMN_COUNT = 7;
const DYNAMIC_COLUMN_WIDTH_LARGE = 394;
const DYNAMIC_COLUMN_WIDTH_SMALL = 240;
const COLUMN_THRESHOLD = 8; // 컬럼이 8개 이상일 때 컬럼 너비 계산

interface SessionDownloadModalProps {
  sessionId: number | undefined;
  modalOpen: boolean;
  setModalClose: () => void;
}

export const SessionDownloadModal = ({ sessionId, modalOpen, setModalClose }: SessionDownloadModalProps) => {
  const me = useRecoilValue(meState);
  const [, setToastMsg] = useRecoilState(toastState);

  const { checked, setChecked, tableHeader, filteredTableBody, activitySession, isLoading } = useSessionDownloadData(
    sessionId,
    me?.id,
  );

  // 미제출자 알림 훅
  const { handleSendToUnsubmitted } = useTeacherActivitySessionUnsubmitterNotificate({
    sessionId: sessionId || 0,
    onSuccess: () => setToastMsg('미제출자 알림을 보냈습니다.'),
    onError: () => setToastMsg('미제출자 알림 전송에 실패했습니다.'),
  });

  const hasUnsubmitter = useMemo(() => filteredTableBody?.some((row) => row[4] === '미제출'), [filteredTableBody]);

  // 엑셀 다운로드 핸들러
  const handleFilteredDownload = useCallback(() => {
    if (!tableHeader || !filteredTableBody) return;

    const worksheetData = [tableHeader, ...filteredTableBody];
    const fileName = `${activitySession?.title ?? ''}-${checked ? '미제출자' : '전체 제출자'} 목록`;

    exportToExcel(worksheetData, fileName);
  }, [tableHeader, filteredTableBody, activitySession?.title, checked]);

  // 컬럼 너비 계산
  const calculateColumnWidth = useCallback((index: number, totalColumns: number): number => {
    const isFixedColumn = index < FIXED_COLUMN_COUNT;

    if (isFixedColumn) {
      return FIXED_COLUMN_WIDTHS[index];
    }

    return totalColumns < COLUMN_THRESHOLD ? DYNAMIC_COLUMN_WIDTH_LARGE : DYNAMIC_COLUMN_WIDTH_SMALL;
  }, []);

  // 테이블 컬럼 데이터 생성
  const tableColumns = useMemo((): CellTypes[] => {
    if (!tableHeader) return [];

    return tableHeader.map((cell, index) => ({
      cellData: String(cell),
      width: calculateColumnWidth(index, tableHeader.length),
    }));
  }, [tableHeader, calculateColumnWidth]);

  // 테이블 행 데이터 생성
  const tableRows = useMemo((): TableRowTypes[] => {
    if (!filteredTableBody || !tableHeader) return [];

    return filteredTableBody.map((row) =>
      row.map((cell, index) => ({
        cellData: cell ? String(cell) : '-',
        width: calculateColumnWidth(index, tableHeader.length),
        align: index > 5 ? 'left' : 'center',
      })),
    );
  }, [filteredTableBody, tableHeader, calculateColumnWidth]);

  // 체크박스 변경 핸들러
  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setChecked(event.target.checked);
    },
    [setChecked],
  );

  // 미제출자 알림 보내기
  const notificateHandler = () => {
    handleSendToUnsubmitted();
  };

  const noneUnsubmitter = !hasUnsubmitter && checked;
  const noneUnsubmitterUI = (
    <div className="flex h-full w-full items-center justify-center">
      <Typography variant="body3" className="text-primary-gray-700">
        미제출자가 없습니다.
      </Typography>
    </div>
  );

  return (
    <PopupModal
      modalOpen={modalOpen}
      setModalClose={setModalClose}
      size="large"
      title="제출현황 다운로드"
      contentsClassName="pt-0"
      footerButtons={
        <div className="flex gap-3">
          <ButtonV2
            variant="outline"
            size={48}
            color="gray400"
            children="미제출자 알림 보내기"
            disabled={isLoading || !hasUnsubmitter}
            onClick={notificateHandler}
          />
          <ButtonV2
            variant="solid"
            size={48}
            color="orange800"
            children="엑셀 다운로드"
            disabled={isLoading || noneUnsubmitter}
            onClick={handleFilteredDownload}
          />
        </div>
      }
    >
      <div className="flex items-center justify-end gap-2">
        <Checkbox checked={checked} onChange={handleCheckboxChange} id="checked-not-submitted" />
        <label htmlFor="checked-not-submitted">
          <Typography variant="body3" className="cursor-pointer text-primary-gray-700">
            미제출자만 보기
          </Typography>
        </label>
      </div>

      <div className="scroll-box mt-4 h-[442px] w-full overflow-auto bg-white">
        <Table>
          <TableHeader columns={tableColumns} fixedColumnCount={6} isFixedHeader={true} />
          {!noneUnsubmitter && <TableBody rows={tableRows} fixedColumnCount={6} />}
        </Table>
        {noneUnsubmitter && noneUnsubmitterUI}
      </div>
    </PopupModal>
  );
};
